import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Tabs, Tab, Card, Row, Col, Button, Form, Badge, Modal, Spinner, InputGroup } from 'react-bootstrap';
import { 
  FaPlus, FaSearch, FaTrashAlt, FaEdit, FaEye, 
  FaImage, FaVideo, FaCheckCircle, FaBullhorn, FaInfoCircle,
  FaCloudUploadAlt, FaLink, FaArrowLeft, FaTimes, FaFileMedical,
  FaCalendarAlt, FaTags
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import moment from 'moment';
import axios from 'axios';

const API_BASE = "https://api.collegeall.in/api/v1";
const ASSET_BASE = "https://api.collegeall.in/api/v1"; // Prefix for relative /uploads/ paths

const PosterManager = () => {
  const [key, setKey] = useState('gallery');
  const [posters, setPosters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  
  // Modals
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPoster, setSelectedPoster] = useState(null);
  const [editingPoster, setEditingPoster] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    key_words: '', // Stored as comma-separated in form
    type: 'image',
    upload_method: 'file', // 'file' or 'url'
    media_url: '',
    selectedFile: null,
    file_preview: null
  });

  const fileInputRef = useRef(null);

  const fetchPosters = useCallback(async (query = '') => {
    setLoading(true);
    try {
      let url = `${API_BASE}/posters`;
      if (query) {
        url = `${API_BASE}/posters/filter?keywords=${encodeURIComponent(query)}`;
      }
      const res = await axios.get(url);
      const fetchedData = res.data.data || [];
      setPosters(fetchedData);
    } catch (err) {
      console.error("Fetch Error:", err);
      toast.error('Failed to sync poster gallery.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search logic
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchPosters(searchTerm);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, fetchPosters]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (formData.type === 'image' && !file.type.startsWith('image/')) {
        toast.error('Invalid file: Please select an image.');
        return;
      }
      if (formData.type === 'video' && !file.type.startsWith('video/')) {
        toast.error('Invalid file: Please select a video.');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ 
          ...prev, 
          selectedFile: file,
          file_preview: reader.result 
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isUrlMode = formData.upload_method === 'url';
    if (isUrlMode && !formData.media_url) {
      toast.warn('Please enter a media URL.');
      return;
    }
    if (!isUrlMode && !formData.selectedFile && !editingPoster) {
      toast.warn('Please select a file to upload.');
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('description', formData.description);
      
      // Send as comma separated string - backend handles the json storage
      data.append('key_words', formData.key_words); 
      data.append('type', formData.type);
      data.append('upload_method', formData.upload_method);

      if (isUrlMode) {
        data.append('media_url', formData.media_url);
      } else if (formData.selectedFile) {
        data.append('media_file', formData.selectedFile);
      }

      if (editingPoster) {
        await axios.put(`${API_BASE}/posters/update/${editingPoster.id}`, data);
        toast.success('Campaign updated successfully.');
      } else {
        await axios.post(`${API_BASE}/posters/create`, data);
        toast.success('Campaign launched successfully.');
      }

      fetchPosters(searchTerm);
      resetForm();
      setKey('gallery');
    } catch (err) {
      console.error("Submit Error:", err);
      toast.error(err.response?.data?.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedPoster) return;
    setLoading(true);
    try {
      await axios.delete(`${API_BASE}/posters/delete/${selectedPoster.id}`);
      setPosters(posters.filter(p => p.id !== selectedPoster.id));
      setShowDeleteModal(false);
      toast.success('Asset removed from library.');
    } catch (err) {
      console.error("Delete Error:", err);
      toast.error('Failed to delete asset.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ 
      title: '', 
      description: '', 
      key_words: '',
      type: 'image', 
      upload_method: 'file', 
      media_url: '', 
      selectedFile: null,
      file_preview: null 
    });
    setEditingPoster(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const startEdit = (poster) => {
    setEditingPoster(poster);
    
    // Parse keywords for the form if they come back as stringified JSON
    let keywordVal = poster.key_words || poster.keywords || '';
    if (typeof keywordVal === 'string' && keywordVal.startsWith('[')) {
      try {
        const parsed = JSON.parse(keywordVal);
        keywordVal = Array.isArray(parsed) ? parsed.join(', ') : keywordVal;
      } catch (e) {
        // use as is
      }
    }

    setFormData({
      title: poster.title,
      description: poster.description,
      key_words: keywordVal,
      type: poster.type,
      upload_method: poster.upload_method || 'file',
      media_url: poster.upload_method === 'url' ? poster.media_url : '',
      selectedFile: null,
      file_preview: poster.upload_method === 'file' ? (poster.media_url.startsWith('http') ? poster.media_url : `${ASSET_BASE}${poster.media_url}`) : null
    });
    setKey('create');
  };

  const filteredPosters = useMemo(() => {
    // Only local filter for Type since search is handled by API
    return posters.filter(p => {
      const matchesType = !filterType || p.type === filterType;
      return matchesType;
    });
  }, [posters, filterType]);

  const getFullMediaUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    return `${ASSET_BASE}${url}`;
  };

  const parseKeywords = (keywords) => {
    if (!keywords) return [];
    if (Array.isArray(keywords)) return keywords;
    try {
      const parsed = JSON.parse(keywords);
      return Array.isArray(parsed) ? parsed : [keywords];
    } catch (e) {
      return keywords.split(',').map(k => k.trim()).filter(Boolean);
    }
  };

  const renderMedia = (type, url, isPreview = false) => {
    const fullUrl = getFullMediaUrl(url);
    if (!fullUrl) return <div className="empty-preview d-flex flex-column align-items-center justify-content-center h-100 bg-slate-100 rounded text-slate-400 p-4"><FaFileMedical size={30} className="mb-2 opacity-50"/><span className="small italic fw-medium">Preview Pending</span></div>;
    
    if (type === 'video') {
      return (
        <video 
          src={fullUrl} 
          controls={!isPreview} 
          autoPlay={isPreview}
          muted={isPreview}
          loop={isPreview}
          className="w-100 h-100 object-fit-cover rounded shadow-inner"
          style={{ background: '#000' }}
        />
      );
    }
    return (
      <img 
        src={fullUrl} 
        alt="Poster" 
        className="w-100 h-100 object-fit-cover rounded shadow-inner"
        onError={(e) => { e.target.src = 'https://upload.wikimedia.org/wikipedia/commons/a/a3/Image-not-found.png'; }}
      />
    );
  };

  return (
    <div className="poster-mgmt-root p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="fw-bold text-slate-900 mb-1">Creative Campaign Studio</h2>
          <p className="text-muted small mb-0">Control visual communication assets across the platform</p>
        </div>
        <Button variant="primary" className="btn-modern-indigo shadow-sm" onClick={() => { resetForm(); setKey('create'); }}>
          <FaPlus className="me-2" /> New Design
        </Button>
      </div>

      {/* Stats Row */}
      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="border-0 shadow-sm metric-box p-3">
            <div className="d-flex align-items-center">
              <div className="icon-wrap bg-indigo-50 text-indigo-600"><FaBullhorn /></div>
              <div className="ms-3">
                <div className="tiny-label text-muted fw-bold uppercase">Total Assets</div>
                <h4 className="fw-bold mb-0">{posters.length}</h4>
              </div>
            </div>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm metric-box p-3">
            <div className="d-flex align-items-center">
              <div className="icon-wrap bg-emerald-50 text-emerald-600"><FaImage /></div>
              <div className="ms-3">
                <div className="tiny-label text-muted fw-bold uppercase">Static Images</div>
                <h4 className="fw-bold mb-0">{posters.filter(p => p.type === 'image').length}</h4>
              </div>
            </div>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm metric-box p-3">
            <div className="d-flex align-items-center">
              <div className="icon-wrap bg-amber-50 text-amber-600"><FaVideo /></div>
              <div className="ms-3">
                <div className="tiny-label text-muted fw-bold uppercase">Video Motion</div>
                <h4 className="fw-bold mb-0">{posters.filter(p => p.type === 'video').length}</h4>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
        <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3">
          <Tabs activeKey={key} onSelect={(k) => setKey(k)} className="custom-tabs border-0">
            <Tab eventKey="gallery" title="Campaign Gallery" />
            <Tab eventKey="create" title={editingPoster ? "Modify Asset" : "Media Designer"} />
          </Tabs>

          {key === 'gallery' && (
            <div className="d-flex gap-2 align-items-center">
              <Form.Select size="sm" className="select-modern border-0 bg-light fw-bold" style={{ width: '130px' }} value={filterType} onChange={e => setFilterType(e.target.value)}>
                <option value="">All Formats</option>
                <option value="image">Still Image</option>
                <option value="video">Short Video</option>
              </Form.Select>
              <InputGroup className="search-pill">
                <InputGroup.Text className="bg-light border-0 ps-3"><FaSearch className="text-muted small" /></InputGroup.Text>
                <Form.Control 
                  className="bg-light border-0 small shadow-none" 
                  placeholder="Search by keywords..." 
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </div>
          )}
        </div>

        <div className="tab-content">
          {key === 'gallery' ? (
            <div className="p-4 bg-slate-50">
              {loading && posters.length === 0 ? (
                <div className="text-center py-5"><Spinner animation="grow" variant="primary" /></div>
              ) : filteredPosters.length === 0 ? (
                <div className="text-center py-5 text-muted opacity-50">
                  <FaFileMedical size={50} className="mb-3" />
                  <p className="fw-semibold">No posters matching filters found.</p>
                  <Button variant="link" onClick={() => { setSearchTerm(''); setFilterType(''); }} className="text-indigo-600 text-decoration-none small fw-bold">Clear Filters</Button>
                </div>
              ) : (
                <Row className="g-4">
                  {filteredPosters.map((poster) => (
                    <Col key={poster.id} lg={4} md={6}>
                      <Card className="h-100 border-0 shadow-sm poster-grid-card overflow-hidden">
                        <div className="poster-view-port">
                          {renderMedia(poster.type, poster.media_url, true)}
                          <div className="poster-hover-actions">
                             <div className="d-flex gap-2">
                               <button className="icon-btn-round" title="View Detail" onClick={() => { setSelectedPoster(poster); setShowDetailModal(true); }}><FaEye /></button>
                               <button className="icon-btn-round" title="Edit" onClick={() => startEdit(poster)}><FaEdit /></button>
                               <button className="icon-btn-round danger" title="Delete" onClick={() => { setSelectedPoster(poster); setShowDeleteModal(true); }}><FaTrashAlt /></button>
                             </div>
                          </div>
                          <Badge bg={poster.type === 'video' ? 'warning' : 'primary'} className="format-pill">
                            {poster.type.toUpperCase()}
                          </Badge>
                        </div>
                        <Card.Body className="p-3">
                          <h6 className="fw-bold text-slate-900 mb-1">{poster.title}</h6>
                          <p className="text-muted tiny-text mb-3 line-clamp-2">{poster.description}</p>
                          <div className="d-flex justify-content-between align-items-center mt-auto border-top pt-2">
                             <span className="x-small text-muted fw-medium d-flex align-items-center">
                               <FaCalendarAlt className="me-1 opacity-50"/> {poster.cdate ? moment(poster.cdate).fromNow() : 'Recently'}
                             </span>
                             <button className="btn-text-only" onClick={() => { setSelectedPoster(poster); setShowDetailModal(true); }}>EXPLORE</button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </div>
          ) : (
            <div className="p-4 bg-white">
              <Form onSubmit={handleSubmit}>
                <Row className="g-4">
                  <Col lg={7}>
                    <div className="form-section-header mb-3">Creative Meta</div>
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-bold text-slate-600">POSTER TITLE</Form.Label>
                      <Form.Control 
                        className="modern-field" 
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required 
                        placeholder="e.g. Summer Orientation 2025"
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-bold text-slate-600">TAGS / KEYWORDS</Form.Label>
                      <InputGroup>
                        <InputGroup.Text className="bg-light border-end-0 text-muted"><FaTags size={14}/></InputGroup.Text>
                        <Form.Control 
                          className="modern-field border-start-0 ps-0" 
                          name="key_words"
                          value={formData.key_words}
                          onChange={handleInputChange}
                          placeholder="Search keywords separated by commas (e.g. tech, jobs, hiring)"
                        />
                      </InputGroup>
                      <Form.Text className="text-muted x-small">These help users discover this content via search.</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-4">
                      <Form.Label className="small fw-bold text-slate-600">NARRATIVE / DESCRIPTION</Form.Label>
                      <Form.Control 
                        as="textarea" 
                        rows={4} 
                        className="modern-field" 
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        required 
                        placeholder="Context and goal for this visual piece..."
                      />
                    </Form.Group>

                    <div className="form-section-header mb-3">Media Configuration</div>
                    <Row className="g-3">
                       <Col md={6}>
                          <Form.Group>
                            <Form.Label className="small fw-bold text-slate-600">MEDIA FORMAT</Form.Label>
                            <div className="d-flex gap-2">
                               <div className={`selector-chip ${formData.type === 'image' ? 'active' : ''}`} onClick={() => setFormData(p => ({ ...p, type: 'image' }))}>
                                  <FaImage className="me-2" /> Static
                               </div>
                               <div className={`selector-chip ${formData.type === 'video' ? 'active' : ''}`} onClick={() => setFormData(p => ({ ...p, type: 'video' }))}>
                                  <FaVideo className="me-2" /> Motion
                               </div>
                            </div>
                          </Form.Group>
                       </Col>
                       <Col md={6}>
                          <Form.Group>
                            <Form.Label className="small fw-bold text-slate-600">ACQUISITION SOURCE</Form.Label>
                            <div className="d-flex gap-2">
                               <div className={`selector-chip ${formData.upload_method === 'file' ? 'active' : ''}`} onClick={() => setFormData(p => ({ ...p, upload_method: 'file' }))}>
                                  <FaCloudUploadAlt className="me-2" /> Upload
                               </div>
                               <div className={`selector-chip ${formData.upload_method === 'url' ? 'active' : ''}`} onClick={() => setFormData(p => ({ ...p, upload_method: 'url' }))}>
                                  <FaLink className="me-2" /> Remote
                               </div>
                            </div>
                          </Form.Group>
                       </Col>
                       <Col xs={12}>
                          {formData.upload_method === 'url' ? (
                            <Form.Group>
                              <Form.Label className="small fw-bold text-slate-600">DIRECT RESOURCE URL</Form.Label>
                              <Form.Control 
                                className="modern-field" 
                                name="media_url"
                                value={formData.media_url}
                                onChange={handleInputChange}
                                placeholder="https://upload.wikimedia.org/wikipedia/commons/a/a3/Image-not-found.png"
                              />
                            </Form.Group>
                          ) : (
                            <Form.Group>
                              <Form.Label className="small fw-bold text-slate-600">LOCAL FILE SELECTION</Form.Label>
                              <div className="file-drop-area" onClick={() => fileInputRef.current.click()}>
                                {formData.file_preview ? (
                                  <div className="file-selected p-3 bg-indigo-50 border border-indigo-200 rounded-3 d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center gap-2">
                                       <FaCheckCircle className="text-indigo-600" />
                                       <span className="small fw-bold text-indigo-900">Resource Attached</span>
                                    </div>
                                    <button type="button" className="btn btn-sm text-danger" onClick={(e) => { e.stopPropagation(); setFormData(p => ({ ...p, file_preview: null, selectedFile: null })); }}><FaTimes /></button>
                                  </div>
                                ) : (
                                  <div className="upload-placeholder py-4 text-center">
                                    <FaCloudUploadAlt size={36} className="text-slate-300 mb-2" />
                                    <div className="small fw-bold text-slate-500">Click to browse {formData.type} files</div>
                                    <div className="tiny-text text-muted mt-1">High-quality JPG, PNG or MP4 up to 25MB</div>
                                  </div>
                                )}
                                <input type="file" ref={fileInputRef} className="d-none" onChange={handleFileChange} accept={formData.type === 'image' ? "image/*" : "video/*"} />
                              </div>
                            </Form.Group>
                          )}
                       </Col>
                    </Row>
                  </Col>

                  <Col lg={5}>
                    <div className="preview-sticky">
                      <div className="form-section-header mb-3">Live Canvas</div>
                      <div className="live-canvas shadow-lg border rounded-4 overflow-hidden bg-slate-900">
                         {renderMedia(formData.type, formData.upload_method === 'url' ? formData.media_url : formData.file_preview)}
                      </div>
                      <div className="mt-4 d-grid gap-2">
                        <Button variant="primary" type="submit" className="fw-bold py-3 btn-modern-indigo shadow-sm" disabled={loading}>
                          {loading ? <Spinner animation="border" size="sm" className="me-2" /> : <FaCheckCircle className="me-2" />}
                          {editingPoster ? 'UPDATE CAMPAIGN' : 'PUBLISH DESIGN'}
                        </Button>
                        <Button variant="outline-slate" className="fw-bold py-2 border-0" onClick={() => { resetForm(); setKey('gallery'); }}>
                           ABORT CHANGES
                        </Button>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Form>
            </div>
          )}
        </div>
      </Card>

      {/* Asset Insight Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="xl" centered className="insight-modal">
        <Modal.Header closeButton className="border-0 pb-0" />
        <Modal.Body className="p-4 pt-2">
          {selectedPoster && (
            <Row className="g-4">
              <Col lg={7}>
                <div className="detail-media-frame bg-slate-900 shadow-2xl rounded-4 overflow-hidden">
                  {renderMedia(selectedPoster.type, selectedPoster.media_url)}
                </div>
              </Col>
              <Col lg={5}>
                <div className="d-flex flex-column h-100">
                  <div className="mb-4">
                    <Badge bg="indigo-subtle" className="px-3 py-2 mb-3 border-0">
                       <span className="text-indigo-700 uppercase">{selectedPoster.type || 'ASSET'} COMPONENT</span>
                    </Badge>
                    <h2 className="fw-bold text-slate-900 mb-2">{selectedPoster.title}</h2>
                    
                    {/* Keywords Section */}
                    <div className="d-flex flex-wrap gap-1 mb-3">
                       { (selectedPoster.key_words || selectedPoster.keywords) ? parseKeywords(selectedPoster.key_words || selectedPoster.keywords).map((tag, idx) => (
                         <Badge key={idx} bg="light" text="dark" className="border font-monospace x-small">#{tag.trim()}</Badge>
                       )) : <span className="text-muted x-small italic">No keywords assigned</span>}
                    </div>

                    {/* Description Section - Below Keywords */}
                    <div className="section-label mb-2 mt-4">Description</div>
                    <div className="description-scroll-area pr-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                       <p className="text-muted leading-relaxed whitespace-pre-wrap">{selectedPoster.description}</p>
                    </div>
                  </div>
                  
                  <div className="mt-auto">
                    <div className="insight-grid mb-4">
                       <div className="insight-node p-3 rounded-4 bg-slate-50 border">
                          <div className="tiny-label text-muted fw-bold mb-1 uppercase">Source Profile</div>
                          <div className="small fw-bold text-slate-800 uppercase">{selectedPoster.upload_method || 'EXTERNAL'}</div>
                       </div>
                       <div className="insight-node p-3 rounded-4 bg-slate-50 border">
                          <div className="tiny-label text-muted fw-bold mb-1 uppercase">Published Date</div>
                          <div className="small fw-bold text-slate-800">{selectedPoster.cdate ? moment(selectedPoster.cdate).format('DD MMM YYYY') : 'N/A'}</div>
                       </div>
                    </div>
                    <div className="d-flex gap-2">
                      <Button variant="primary" className="flex-grow-1 fw-bold btn-modern-indigo" onClick={() => { setShowDetailModal(false); startEdit(selectedPoster); }}>MODIFY DESIGN</Button>
                      <Button variant="danger" className="fw-bold" onClick={() => { setShowDetailModal(false); setShowDeleteModal(true); }}><FaTrashAlt /></Button>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
      </Modal>

      {/* Removal Confirmation */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="delete-confirm-modal">
        <Modal.Body className="p-4 text-center">
          <div className="danger-icon-wrapper mb-3 shadow-sm"><FaTrashAlt /></div>
          <h5 className="fw-bold text-slate-900">Archive Permanently?</h5>
          <p className="text-muted small">You are removing <strong>{selectedPoster?.title}</strong> from active production. This workflow is non-reversible.</p>
          <div className="d-grid gap-2 mt-4">
            <Button variant="danger" className="fw-bold py-2" onClick={confirmDelete}>YES, ARCHIVE ASSET</Button>
            <Button variant="light" onClick={() => setShowDeleteModal(false)} className="text-muted fw-bold border-0 bg-transparent">CANCEL</Button>
          </div>
        </Modal.Body>
      </Modal>

      <style>{`
        .poster-mgmt-root { background: #f8fafc; min-height: 100vh; font-family: 'Inter', system-ui, -apple-system, sans-serif; }
        .metric-box { border: 1px solid #e2e8f0 !important; border-radius: 1rem; }
        .icon-wrap { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; }
        .btn-modern-indigo { background: #6366f1; border: none; border-radius: 0.75rem; font-weight: 700; font-size: 0.85rem; padding: 0.7rem 1.5rem; transition: all 0.2s; }
        .btn-modern-indigo:hover { background: #4f46e5; transform: translateY(-1px); }
        .custom-tabs .nav-link { color: #64748b; border: 0; font-size: 0.85rem; font-weight: 700; padding: 1rem 1.5rem; position: relative; }
        .custom-tabs .nav-link.active { color: #6366f1; background: transparent; }
        .custom-tabs .nav-link.active::after { content: ''; position: absolute; bottom: 0; left: 1rem; right: 1rem; height: 3px; background: #6366f1; border-radius: 2px; }
        
        .poster-grid-card { border-radius: 1.25rem !important; transition: all 0.3s; border: 1px solid #e2e8f0 !important; background: white; }
        .poster-grid-card:hover { transform: translateY(-4px); box-shadow: 0 15px 30px -10px rgba(0,0,0,0.1) !important; }
        
        .poster-view-port { position: relative; height: 190px; background: #f1f5f9; overflow: hidden; }
        .poster-hover-actions { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(15, 23, 42, 0.5); display: flex; align-items: center; justify-content: center; opacity: 0; transition: 0.3s; backdrop-filter: blur(3px); }
        .poster-view-port:hover .poster-hover-actions { opacity: 1; }
        
        .icon-btn-round { width: 36px; height: 36px; border-radius: 50%; border: 0; background: white; color: #0f172a; display: flex; align-items: center; justify-content: center; transition: 0.2s; font-size: 0.9rem; }
        .icon-btn-round:hover { transform: scale(1.1); box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
        .icon-btn-round.danger:hover { background: #ef4444; color: white; }
        
        .format-pill { position: absolute; top: 12px; right: 12px; font-size: 0.6rem; padding: 5px 9px; border-radius: 6px; font-weight: 800; letter-spacing: 0.05em; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        .form-section-header { font-size: 0.65rem; font-weight: 800; text-transform: uppercase; color: #94a3b8; border-left: 3px solid #6366f1; padding-left: 10px; letter-spacing: 0.05em; }
        .modern-field { border-radius: 0.75rem !important; border: 1.5px solid #f1f5f9 !important; background: #f8fafc !important; font-size: 0.9rem; padding: 0.75rem 1rem; }
        .modern-field:focus { border-color: #6366f1 !important; background: white !important; box-shadow: none; }
        
        .selector-chip { flex: 1; padding: 0.75rem; background: #f1f5f9; border-radius: 0.75rem; display: flex; align-items: center; justify-content: center; font-size: 0.8rem; font-weight: 700; cursor: pointer; border: 2px solid transparent; transition: all 0.2s; }
        .selector-chip.active { background: #eef2ff; border-color: #6366f1; color: #6366f1; }
        
        .upload-placeholder { border: 2px dashed #e2e8f0; border-radius: 0.75rem; background: #fafafa; cursor: pointer; transition: 0.2s; }
        .upload-placeholder:hover { border-color: #6366f1; background: #f9faff; }
        
        .live-canvas { width: 100%; height: 320px; display: flex; align-items: center; justify-content: center; }
        .preview-sticky { position: sticky; top: 90px; }
        
        .insight-modal .modal-content { border-radius: 2rem; border: 0; overflow: hidden; }
        .detail-media-frame { height: 420px; display: flex; align-items: center; justify-content: center; }
        .insight-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
        
        .danger-icon-wrapper { width: 60px; height: 60px; background: #fee2e2; color: #ef4444; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto; font-size: 1.4rem; }
        .tiny-label { font-size: 0.6rem; letter-spacing: 0.05em; }
        .tiny-text { font-size: 0.72rem; line-height: 1.5; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .btn-text-only { background: transparent; border: 0; color: #6366f1; font-weight: 800; font-size: 0.7rem; padding: 0; letter-spacing: 0.05em; }
        .btn-text-only:hover { text-decoration: underline; }
        
        .bg-indigo-50 { background-color: #eef2ff; }
        .text-indigo-600 { color: #4f46e5; }
        .bg-indigo-subtle { background-color: #e0e7ff; }
        .text-indigo-700 { color: #3730a3; }
        .search-pill { max-width: 250px; border-radius: 50px; overflow: hidden; background: #f1f5f9; border: 1px solid #e2e8f0; }
        .select-modern { border-radius: 50px !important; font-size: 0.75rem !important; }
        .uppercase { text-transform: uppercase; }
        .whitespace-pre-wrap { white-space: pre-wrap; }
        .description-scroll-area::-webkit-scrollbar { width: 4px; }
        .description-scroll-area::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
      `}</style>
    </div>
  );
};

export default PosterManager;